import { authManager } from './auth';

// Program flag "Frontend Card Display" fields — saved under config.display
// and read by the member frontend's /benefits page (Benefit.js), plus the
// MemberDashboard "Your Programs" and PricingPlan "CircleOne Benefits" tiles.
//
// The /benefits redesign added comingSoon, notifyMe, highlightBadge,
// filterCategories, audience and hideCta. All are optional and default to the
// pre-redesign behaviour, so flags saved before them render exactly as they did.

// Filter chips on /benefits. `value` is what the member frontend matches on.
// KEEP IN SYNC with BENEFIT_FILTERS in somo-circle1-membership-frontend
// src/Screens/Benefit.js — displayConfigFromFlag drops values not listed
// here, so a category added only on the member site would be stripped from
// the flag on the next admin save.
export const BENEFIT_FILTERS = [
    { value: 'money', label: 'Money & planning' },
    { value: 'support', label: 'Support & protection' },
    { value: 'everyday', label: 'Everyday savings' },
    { value: 'travel', label: 'Education & travel' },
];

const FILTER_VALUES = BENEFIT_FILTERS.map((f) => f.value);

export const EMPTY_DISPLAY_CONFIG = {
    showAsProgram: false,
    displayName: '',
    subtitle: '',
    icon: '',
    colorClass: '',
    cardDescription: '',
    badge: '',
    path: '',
    // Not live yet: amber badge, no "Access Now" button on /benefits, and
    // left out of the dashboard/pricing tiles (nothing to open). Frontend
    // Path is optional.
    comingSoon: false,
    // Coming-soon card shows "Notify me": visitors leave an email, and the
    // admin sends them one launch email once the card is live (api).
    notifyMe: false,
    // Amber badge on a live program (e.g. "New").
    highlightBadge: false,
    // Live card with its normal badge but no "Access Now" button (nothing to
    // open yet, e.g. Pharmacy before its page exists). Like comingSoon it is
    // left out of the dashboard/pricing tiles, and Frontend Path is optional.
    hideCta: false,
    filterCategories: [],
    // Card footer label, e.g. "Whole household".
    audience: '',
};

export const displayConfigFromFlag = (display = {}) => ({
    showAsProgram: Boolean(display.showAsProgram),
    displayName: display.displayName || '',
    subtitle: display.subtitle || '',
    icon: display.icon || '',
    colorClass: display.colorClass || '',
    cardDescription: display.cardDescription || '',
    badge: display.badge || '',
    path: display.path || '',
    comingSoon: Boolean(display.comingSoon),
    notifyMe: Boolean(display.notifyMe),
    highlightBadge: Boolean(display.highlightBadge),
    hideCta: Boolean(display.hideCta),
    filterCategories: Array.isArray(display.filterCategories)
        ? display.filterCategories.filter((c) => FILTER_VALUES.includes(c))
        : [],
    audience: display.audience || '',
});

// Returns an error message, or '' when the display config can be saved.
export const validateDisplayConfig = (display) => {
    if (!display.showAsProgram) return '';
    if (!display.displayName.trim()) {
        return 'Display Name is required when showing as a program card';
    }
    const { path } = display;
    if (!path.trim()) {
        return (display.comingSoon || display.hideCta)
            ? ''
            : 'Frontend Path is required when showing as a program card (or mark it Coming Soon, or hide the "Access Now" button)';
    }
    // Internal route ("/goaltide") or external partner link
    // ("https://fraudster.ai/circleone" — opens in a new tab).
    if (!path.startsWith('/') && !/^https?:\/\//i.test(path)) {
        return 'Frontend Path must start with / (internal route) or http(s):// (external link)';
    }
    return '';
};

// Non-blocking advice shown on save ('' when none). The member site only
// renders a badge when Badge Text is set, so Coming Soon / Highlight Badge
// without it would show no badge at all.
export const displayConfigWarning = (display) => {
    if (!display.showAsProgram || display.badge.trim()) return '';
    if (display.comingSoon) return 'Saved, but Coming Soon has no Badge Text — the card will show no "Coming soon" badge. Add Badge Text (e.g. "Coming 11/1").';
    if (display.highlightBadge) return 'Saved, but Highlight Badge has no Badge Text — no badge will show. Add Badge Text (e.g. "New").';
    return '';
};

// The saved flag is live on /benefits — the launch email can be sent.
// Mirrors isLive in somo-circle1-api's benefitNotify.service.js, including
// the optional schedule window.
export const isLiveOnBenefits = (flag, now = new Date()) => {
    if (!flag) return false;
    const { startDate, endDate } = flag.schedule || {};
    return flag.status === 'active'
        && flag.isGloballyEnabled === true
        && flag.config?.display?.showAsProgram === true
        && flag.config?.display?.comingSoon !== true
        && !(startDate && new Date(startDate) > now)
        && !(endDate && new Date(endDate) < now);
};

const notifyUrl = (id, suffix = '') => `${process.env.REACT_APP_API_BASE_URL}/admin/program-flags/${encodeURIComponent(id)}/notify-signups${suffix}`;

const notifyRequest = async (url, options) => {
    const res = await authManager.authenticatedFetch(url, options);
    const body = await res.json().catch(() => null);
    if (body?.status === 'success') return body.data;
    throw new Error(body?.message || `Request failed (${res.status})`);
};

// { waiting, notified }
export const getNotifySignupCounts = (id) => notifyRequest(notifyUrl(id));

// Sends ONE batch (the API caps it and waits for SES) → { sent, failed, remaining }.
export const sendNotifyLaunchEmail = (id) => notifyRequest(notifyUrl(id, '/send'), { method: 'POST' });

/**
 * Sends batches while they make progress (sent + failed + skipped > 0) and
 * someone is still waiting. A failed address is not retried for a few
 * minutes (API RETRY_DELAY_MS), so a run ends once only failed/cooling-down
 * addresses are left — it can't loop forever. onProgress({ sent, remaining,
 * batchSent }) runs after each batch. Resolves { sent, failed, remaining }
 * as totals for the run.
 */
export const sendAllLaunchEmailBatches = async (id, onProgress = () => {}, totals = { sent: 0, failed: 0 }) => {
    const batch = await sendNotifyLaunchEmail(id);
    const sent = totals.sent + batch.sent;
    const failed = totals.failed + batch.failed;
    onProgress({ sent, remaining: batch.remaining, batchSent: batch.sent });
    const progressed = batch.sent + batch.failed + (batch.skipped || 0) > 0;
    if (batch.remaining === 0 || !progressed) {
        return { sent, failed, remaining: batch.remaining };
    }
    return sendAllLaunchEmailBatches(id, onProgress, { sent, failed });
};
